import { useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BILL_GROUP_INVOICE_FREQUENCY_MAP,
  BillGroupDunningStatusEnum,
  BillGroupFormData,
  BillGroupFormSchema,
  CustomFieldEntityEnum,
  FirstInvoiceCreationStrategyEnum,
  IAccountRespSchema,
  IBillGroupReq,
  IBillGroupResp,
  IContactRespSchema,
  IDunnings,
  ILegalEntityResponseSchema,
  InvoiceCreationEnum,
  IPaymentMethodResp,
  NET_TERMS_DISPLAY,
  OUTPUT_LANGUAGE_MAP,
  PaymentMethodStatusEnum,
  PaymentMethodTypeEnum,
  QuoteSettingsDefaultAddressSourceEnum,
} from '@monetize/types/app';
import {
  ApiListResponse,
  DEFAULT_PAGER,
  FilterType,
  FilterTypeOperator,
  GetListApiFilter,
} from '@monetize/types/common';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { toDateShort } from '@monetize/utils/core';
import { format as formatDate } from 'date-fns/format';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdOutlineError } from 'react-icons/md';
import { useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import {
  getAccountById,
  useGetPaymentMethodsByAccountWithGroupedItems,
} from '../../../../../api/accountsService';
import { apiPatch, handleApiErrorToast } from '../../../../../api/axios';
import { doGetContactById } from '../../../../../api/contactsService';
import {
  useGetById,
  useGetListData,
  useUpdateEntity,
} from '../../../../../api/queryUtils';
import {
  ILegalEntitiesSelector,
  selectLegalEntities,
} from '../../../../../api/selectors';
import { useGetDunningProcesses } from '../../../../../api/settingsService';
import { AccountAddressSelect } from '../../../../../components/Account/AccountAddressSelect';
import { AccountSelect } from '../../../../../components/Account/AccountSelect';
import { ContactSelect } from '../../../../../components/Contacts/ContactSelect';
import { CustomFieldDataForm } from '../../../../../components/CustomFields/CustomFieldDataForm';
import {
  LanguageLocaleDropdown,
  MAlert,
  MBox,
  MButton,
  MCheckbox,
  MCurrencySelect,
  MDivider,
  MFlex,
  MFormField,
  MIcon,
  MInput,
  MLockedTextOrContent,
  MRadio,
  MRadioGroup,
  MSimpleGrid,
  MStack,
  MText,
  MTextarea,
  MTooltip,
} from '../../../../../components/Monetize';
import { BlankText } from '../../../../../components/Monetize/BlankText';
import { DateDisplay } from '../../../../../components/Monetize/DateDisplay';
import { InvoiceDelayInput } from '../../../../../components/Monetize/InvoiceDelayInput';
import { MainSearchInput } from '../../../../../components/Monetize/MCustomSelect/components/MainSearchInput';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { useCustomSelectValue } from '../../../../../components/Monetize/MCustomSelect/useCustomSelectValue';
import { MTooltipContentList } from '../../../../../components/Monetize/MTooltipContentList';
import { AddPaymentMethodForm } from '../../../../../components/PaymentMethods/Form/AddPaymentMethodForm';
import { PaymentMethodItemRenderer } from '../../../../../components/PaymentMethods/PaymentMethodItemRenderer';
import { PAYMENT_METHODS } from '../../../../../constants';
import { BILL_GROUP_STATUS_DISPLAY } from '../../../../../constants/billGroups';
import { ACH_OR_BANK_TRANSFER } from '../../../../../constants/paymentMethods';
import {
  SETTING_INVOICE_ACCOUNTING_DATE_OPTIONS,
  SETTING_INVOICE_CREATION_DISPLAY,
  SETTING_INVOICE_CREATION_OPTIONS,
} from '../../../../../constants/settings';
import { useExperienceConfiguration, useNetTerms } from '../../../../../hooks';
import useCurrentPaymentGateways from '../../../../../hooks/useCurrentPaymentGateways';
import { useACL } from '../../../../../services/acl/acl';
import { useFlags } from '../../../../../services/launchDarkly';
import Sentry from '../../../../../services/sentry';
import { appGlobalDataState } from '../../../../../store/global.store';
import { buildFilterParamsRequestObject } from '../../../../../utils';
import {
  getAddress,
  getAddressFromContact,
} from '../../../../../utils/address';
import {
  formatInvoiceDelay,
  INVOICE_DELAY_TOOLTIP,
} from '../../../../../utils/invoiceDelay';
import { arrayToObject } from '../../../../../utils/misc';
import {
  invoiceBillDateSettingsTooltipItems,
  invoiceBillDateSettingsTooltipTitle,
} from '../../../../Invoices/constants';
import { renderItemContentForAccount } from '../../../../Settings/rules-v3/rules.utils';
import { SectionHeader } from '../../components/SectionHeader';
import {
  AUTO_PAYMENT_COLLECTION_TIMING_DISPLAY,
  AUTO_PAYMENT_COLLECTION_TIMING_OPTIONS,
} from '../../constants';
import {
  buildBillGroupPatchPayload,
  convertBillGroupRespToFormData,
} from '../../utils';
import { BillGroupDetailHeader } from './BillGroupDetailHeader';

interface BillGroupDetailsTabProps {
  billGroup: IBillGroupResp;
  setInvoiceDelay: (val: number) => void;
  isReadOnly?: boolean;
  canEditInvoiceTransformationConfig: boolean;
  onOpenInvoiceTransformationDrawer: () => void;
}

export const BillGroupDetailsTab = ({
  billGroup,
  setInvoiceDelay,
  isReadOnly,
  canEditInvoiceTransformationConfig,
  onOpenInvoiceTransformationDrawer,
}: BillGroupDetailsTabProps) => {
  const { canDo } = useACL();
  const { activeNetTerms, defaultNetTerm } = useNetTerms(billGroup.netTerms);

  const { showInvoiceAccountingDate, enableAutoPaymentTiming } = useFlags();

  const tenantSettings = useExperienceConfiguration(
    ExperienceConfigurationKeys.tenantSettings,
  );

  const convertedBillGroup = convertBillGroupRespToFormData(
    billGroup,
    defaultNetTerm,
  );

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BillGroupFormData>({
    resolver: zodResolver(BillGroupFormSchema),
    mode: 'onChange',
    defaultValues: convertedBillGroup,
    values: convertedBillGroup,
    resetOptions: {
      keepDirtyValues: true,
      keepErrors: true,
    },
  });

  const { data: legalEntities } = useGetListData<
    ILegalEntityResponseSchema,
    ILegalEntitiesSelector
  >(
    'legalEntities',
    {
      config: DEFAULT_PAGER,
    },
    {
      select: selectLegalEntities,
    },
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const handleResetSearchParams = () => {
    if (searchParams.has('editMode')) {
      searchParams.delete('editMode');
      setSearchParams(searchParams);
    }
  };
  const hasEditModeSearchParams = searchParams.has('editMode');
  const [isEditMode, setIsEditMode] = useState<boolean>(
    hasEditModeSearchParams,
  );

  useEffect(() => {
    if (hasEditModeSearchParams) {
      setIsEditMode(true);
    }
  }, [hasEditModeSearchParams]);

  const canOnlyUpdatePONumber = false;

  const isLocked = canOnlyUpdatePONumber || !!billGroup.locked;

  const isReadOnlyProps = {
    isDisabled: isReadOnly,
    disabled: isReadOnly,
    isReadOnly,
    variant: isReadOnly ? 'readonly' : 'outline',
  } as const;

  const isReadOnlyOrLockedProps = {
    isDisabled: isLocked || isReadOnly,
    disabled: isLocked || isReadOnly,
    isReadOnly: isLocked || isReadOnly,
    variant: isLocked || isReadOnly ? 'readonly' : 'outline',
  } as const;

  const {
    name,
    status,
    legalEntityId,
    contactId,
    addressData,
    shippingContactId,
    invoiceDelay,
    invoiceCreation,
    billDay,
    defaultPaymentGatewayId,
    paymentMethodId,
    purchaseOrderNumber,
    registrationNumber,
    vatNumber,
    dunningProcessId,
    strCcEmails,
    strBccEmails,
    currency,
    netTerms,
    customId,
    customFields,
    accountingDateCalculationStrategy,
    linkedAccountId,
    billingAccountId,
    outputLanguage,
    collectInvoiceBalanceAutomatically,
    autoPaymentCollectionTiming,
  } = watch();

  // Read-only fields from billGroup (not part of form state)
  const { invoicingFrequency, invoicingFrequencyInMonths } = billGroup;
  const invoiceTemplateId = undefined; // This field is not yet supported by API

  const [linkedAccount, setLinkedAccount] = useState<IAccountRespSchema | null>(
    null,
  );
  const [shippingAccountId, setShippingAccountId] = useState<string | null>(
    null,
  );

  const accountSelectItems = useMemo(
    () =>
      [
        ...(billGroup.account ? [billGroup.account] : []),
        ...(linkedAccount ? [linkedAccount] : []),
      ].sort((a, b) => (a.id === billingAccountId ? -1 : 1)),
    [billGroup, linkedAccount, billingAccountId],
  );
  const { billingAccount, shippingAccount } = useMemo(() => {
    return {
      billingAccount: accountSelectItems.find(
        (account) => account.id === billingAccountId,
      ),
      shippingAccount: accountSelectItems.find(
        (account) => account.id === shippingAccountId,
      ),
    };
  }, [accountSelectItems, billingAccountId, shippingAccountId]);

  useEffect(() => {
    (async () => {
      if (linkedAccountId) {
        setLinkedAccount(await getAccountById(linkedAccountId));
      }
    })();
  }, [linkedAccountId]);

  useEffect(() => {
    (async () => {
      if (billGroup.shippingContactId && !shippingAccountId) {
        const shippingContact = await doGetContactById(
          billGroup.shippingContactId,
        );
        setShippingAccountId(shippingContact.accountId);
      }
    })();
  }, [billGroup.shippingContactId, shippingAccountId]);

  const handleChangeLinkedAccount = (account: IAccountRespSchema | null) => {
    setValue('linkedAccountId', account?.id, { shouldDirty: true });

    if (account?.id !== billingAccountId) {
      setValue('billingAccountId', billGroup.accountId, { shouldDirty: true });
      setValue('contactId', '', { shouldDirty: true });
      setShippingAccountId(billGroup.accountId);
      setValue('shippingContactId', '', { shouldDirty: true });
      setValue('addressData.billingAddressId', '', { shouldDirty: true });
      setValue('addressData.shippingAddressId', '', { shouldDirty: true });
    }

    setLinkedAccount(account);
  };
  const isAnyCustomFieldsExists = Object.keys(customFields).length > 0;

  const selectedLegalEntity = legalEntities?.legalEntitiesById?.[legalEntityId];
  const invoicingFrequencyDisplay =
    invoicingFrequency &&
    invoicingFrequencyInMonths &&
    BILL_GROUP_INVOICE_FREQUENCY_MAP[invoicingFrequency](
      invoicingFrequencyInMonths,
    );

  const { hasCrmConfigured } = useAtomValue(appGlobalDataState);

  const isAddressSourceIsContact =
    addressData.addressSource === QuoteSettingsDefaultAddressSourceEnum.CONTACT;

  const contactIdCustomValue = useCustomSelectValue<IContactRespSchema>({
    value: contactId,
    setValue: (val) => setValue('contactId', val, { shouldDirty: true }),
    getOneById: doGetContactById,
  });

  const billingAddressTextForAddressSourceContact =
    contactIdCustomValue.internalValue
      ? getAddressFromContact(contactIdCustomValue.internalValue, {
          addressFormat: selectedLegalEntity?.addressFormat,
        }).fullAddress
      : 'Address populates from Billing Contact';

  const shippingContactIdCustomValue = useCustomSelectValue<IContactRespSchema>(
    {
      value: shippingContactId,
      setValue: (val) =>
        setValue('shippingContactId', val, { shouldDirty: true }),
      getOneById: doGetContactById,
    },
  );
  const shippingAddressTextForAddressSourceContact =
    shippingContactIdCustomValue.internalValue
      ? getAddressFromContact(shippingContactIdCustomValue.internalValue, {
          addressFormat: selectedLegalEntity?.addressFormat,
        }).fullAddress
      : 'Address populates from Shipping Contact';

  const { activeGateways, currentPaymentGateway, gatewaysById } =
    useCurrentPaymentGateways({
      refetchOnWindowFocus: false, // If not, the modal flicks whenever window has focus
    });

  const {
    open: isPaymentMethodModalOpen,
    onOpen: onOpenPaymentMethodModal,
    onClose: closePaymentMethodModal,
  } = useDisclosure();

  const onClosePaymentMethodModal = (newPaymentMethod?: IPaymentMethodResp) => {
    closePaymentMethodModal();
    if (newPaymentMethod) {
      setValue('paymentMethodId', newPaymentMethod?.id, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const filters: FilterType[] = [
    {
      key: 'status',
      operator: FilterTypeOperator.EQUAL,
      value: PaymentMethodStatusEnum.ACTIVE,
    },
    {
      key: 'paymentMethodType',
      operator: FilterTypeOperator.NOT_EQUAL,
      value: PaymentMethodTypeEnum.ACH_CREDIT,
    },
  ];

  const paymentMethodsFilterParams: GetListApiFilter =
    buildFilterParamsRequestObject(filters);

  const { data: paymentMethods } =
    useGetPaymentMethodsByAccountWithGroupedItems({
      accountId: billGroup.accountId ?? '',
      filters: paymentMethodsFilterParams,
      filterFn: ({ paymentMethodType }) =>
        !ACH_OR_BANK_TRANSFER.has(paymentMethodType),
    });

  const { data: dunningData } = useGetDunningProcesses<
    ApiListResponse<IDunnings> & { contentById: Record<string, IDunnings> }
  >({
    config: DEFAULT_PAGER,
    options: {
      select: (data) => {
        return {
          ...data,
          contentById: arrayToObject(data.content, 'id'),
        };
      },
    },
  });

  const { data: dunningProcess } = useGetById<IDunnings>(
    'dunnings',
    dunningProcessId ?? '',
    { enabled: !!dunningProcessId, refetchOnWindowFocus: false },
  );

  const dunningStep = (dunningProcess?.dunningSteps || []).find(
    ({ id }) => id && id === billGroup.dunningStepId,
  );

  const {
    mutateAsync: doUpdateBillGroup,
    isPending: isUpdateBillGroupLoading,
  } = useUpdateEntity<IBillGroupResp, Partial<IBillGroupReq>>('billGroups', {
    apiMethodOverride: apiPatch,
    endpointArgs: { accountId: billGroup.accountId },
    onSuccess: (updatedBillGroup) => {
      // `values` keeps dirty fields as typed, so reset explicitly to show what
      // the server saved (e.g. trimmed values and split emails).
      reset(convertBillGroupRespToFormData(updatedBillGroup, defaultNetTerm));
      if (isEditMode) {
        setIsEditMode(false);
      }
    },
    onError: (error) => {
      handleApiErrorToast(error);
    },
  });

  const onSubmit = async (data: BillGroupFormData) => {
    const parsed = BillGroupFormSchema.safeParse(data);
    if (!parsed.success) {
      Sentry.captureException(parsed.error.issues, {
        tags: {
          type: 'BILL_GROUP_FORM',
        },
      });
    }

    const payload = buildBillGroupPatchPayload(convertedBillGroup, data);
    // An empty patch is rejected by the API, and there is nothing to save anyway.
    if (Object.keys(payload).length === 0) {
      // Drop edits that normalized away (e.g. added whitespace).
      reset(convertedBillGroup);
      setIsEditMode(false);
    } else {
      doUpdateBillGroup({ id: billGroup.id, payload });
    }
    handleResetSearchParams();
  };

  const getLanguageLabel = (value: string | null | undefined) => {
    if (!value) {
      return null;
    }
    return OUTPUT_LANGUAGE_MAP[value]?.title || value;
  };

  return (
    <MBox>
      <BillGroupDetailHeader billGroup={billGroup} dunningStep={dunningStep} />
      {billGroup.dunningStatus === BillGroupDunningStatusEnum.PAUSED && (
        <MAlert
          alertIconProps={{ boxSize: 4 }}
          alertDescriptionProps={{ fontSize: 'sm' }}
          alignSelf="stretch"
          type="warning"
        >
          <MFlex>
            <MText mr={1}>
              This bill group is past due, but dunning has been paused until
            </MText>
            <DateDisplay
              date={billGroup.dunningPauseExpirationDate}
              timezoneConfigKey="userTimezone"
            />
            <MText>
              . No automated actions will occur until dunning is resumed
            </MText>
          </MFlex>
        </MAlert>
      )}
      <SectionHeader
        label="Details"
        isEditMode={isEditMode}
        isLoading={isUpdateBillGroupLoading}
        dunningStatus={billGroup.dunningStatus}
        billGroupId={billGroup.id}
        canEdit={canDo([['billing', 'create']])}
        onEdit={() => {
          setIsEditMode((prevState) => !prevState);
          if (isEditMode) {
            reset();
            handleResetSearchParams();
          }
        }}
        onSave={() => {
          handleSubmit(onSubmit)();
        }}
      />

      <MSimpleGrid columns={4} gap={4} mb="6">
        <MFormField error={errors.name} label="Bill Group Name" isRequired>
          <MLockedTextOrContent isLocked={!isEditMode} text={name}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <MInput
                  placeholder="Bill Group #1"
                  {...field}
                  {...isReadOnlyProps}
                  isDisabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
        <MFormField error={errors.status} label="Status">
          <MLockedTextOrContent
            isLocked
            text={BILL_GROUP_STATUS_DISPLAY[status]}
          />
        </MFormField>
        <MFormField
          error={errors.legalEntityId}
          label="Legal Entity"
          isRequired
        >
          <MLockedTextOrContent
            isLocked={!isEditMode}
            text={legalEntities?.legalEntitiesById?.[legalEntityId]?.name}
          >
            <Controller
              name="legalEntityId"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  itemTitle="name"
                  itemValue="id"
                  items={legalEntities?.legalEntities || []}
                  {...field}
                  {...isReadOnlyOrLockedProps}
                  isDisabled={
                    isReadOnlyOrLockedProps.disabled || canOnlyUpdatePONumber
                  }
                  inputProps={{
                    variant: isReadOnlyOrLockedProps.variant,
                    readOnly: isReadOnlyOrLockedProps.isReadOnly,
                    disabled: isReadOnlyOrLockedProps.isDisabled,
                  }}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
        <MFormField error={errors.currency} label="Currency" isRequired>
          <MLockedTextOrContent isLocked={!isEditMode} text={currency}>
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <MCurrencySelect
                  {...field}
                  {...isReadOnlyOrLockedProps}
                  isDisabled={
                    isReadOnlyOrLockedProps.disabled || canOnlyUpdatePONumber
                  }
                  inputProps={{
                    variant: isReadOnlyOrLockedProps.variant,
                    readOnly: isReadOnlyOrLockedProps.isReadOnly,
                    disabled: isReadOnlyOrLockedProps.isDisabled,
                  }}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
        <MFormField error={errors.vatNumber} label="VAT Number">
          <MLockedTextOrContent isLocked={!isEditMode} text={vatNumber}>
            <Controller
              name="vatNumber"
              defaultValue=""
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MInput
                  placeholder="VAT Number"
                  value={value || ''}
                  {...rest}
                  {...isReadOnlyProps}
                  isDisabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>

        {tenantSettings.enableQuoteOutputLanguageSelection && (
          <MFormField
            error={errors.outputLanguage}
            label="Output Language"
            tooltip="Setting the output language will apply to quotes. If you change the setting here, it will be used for any future amendment and renewal quotes."
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={getLanguageLabel(outputLanguage)}
            >
              <Controller
                name="outputLanguage"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <LanguageLocaleDropdown
                    placeholder="Select Output Language"
                    {...field}
                    {...isReadOnlyProps}
                    isDisabled={
                      isReadOnlyProps.disabled || canOnlyUpdatePONumber
                    }
                    inputProps={{
                      variant: isReadOnlyProps.variant,
                      readOnly: isReadOnlyProps.isReadOnly,
                      disabled: isReadOnlyProps.isDisabled,
                    }}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
        )}
        <MFormField
          error={errors.registrationNumber}
          label="Registration Number"
        >
          <MLockedTextOrContent
            isLocked={!isEditMode}
            text={registrationNumber}
          >
            <Controller
              name="registrationNumber"
              defaultValue=""
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MInput
                  placeholder="Registration Number"
                  value={value || ''}
                  {...rest}
                  {...isReadOnlyProps}
                  isDisabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>

        <MFormField error={errors.purchaseOrderNumber} label="PO Number">
          <MLockedTextOrContent
            isLocked={!isEditMode}
            text={purchaseOrderNumber}
          >
            <Controller
              name="purchaseOrderNumber"
              defaultValue=""
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MInput
                  placeholder="PO Number"
                  value={value || ''}
                  {...rest}
                  {...isReadOnlyProps}
                  isDisabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
        <MFormField error={errors.customId} label="Custom ID">
          <MLockedTextOrContent isLocked={!isEditMode} text={customId}>
            <Controller
              name="customId"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MInput
                  value={value || ''}
                  placeholder="Custom ID"
                  maxLength={60}
                  {...rest}
                  {...isReadOnlyProps}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MSimpleGrid>

      <MDivider />
      <SectionHeader label="Contacts" />
      <MText mb="4">
        Changes to Contacts and Addresses impact future created Invoices.
      </MText>
      <MBox mb={6}>
        <MFormField
          error={errors.addressData?.addressSource}
          label="Addresses Sourced From"
          tooltip="Set source of Shipping and Billing Addresses for any future Invoices"
          isHorizontal
          labelProps={{ mb: 0 }}
          mb={4}
        >
          <Controller
            control={control}
            name="addressData.addressSource"
            render={({ field: { value, onChange, ...rest } }) => (
              <MRadioGroup
                onChange={onChange}
                value={value}
                {...rest}
                isDisabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
              >
                <MStack gap={4} direction="row" color="tGray.darkPurple">
                  <MRadio
                    value={QuoteSettingsDefaultAddressSourceEnum.ACCOUNT}
                    isDisabled={
                      isReadOnlyProps.disabled ||
                      !isEditMode ||
                      !hasCrmConfigured ||
                      canOnlyUpdatePONumber
                    }
                    onChange={() => {
                      setValue('addressData.billingAddressId', null);
                      setValue('addressData.shippingAddressId', null);
                    }}
                  >
                    <MTooltip
                      label="CRM must be connected to use Account Addresses"
                      placement="bottom-start"
                      shouldWrapChildren
                      isDisabled={!isEditMode || hasCrmConfigured}
                    >
                      <MText
                        display="inline-flex"
                        alignItems="center"
                        color="currentColor"
                      >
                        Account Addresses
                      </MText>
                    </MTooltip>
                  </MRadio>
                  <MRadio
                    value={QuoteSettingsDefaultAddressSourceEnum.CONTACT}
                    isDisabled={
                      !isEditMode ||
                      isReadOnlyProps.disabled ||
                      canOnlyUpdatePONumber
                    }
                    onChange={() => {
                      setValue('addressData.billingAddressId', null);
                      setValue('addressData.shippingAddressId', null);
                    }}
                  >
                    Contacts
                  </MRadio>
                </MStack>
              </MRadioGroup>
            )}
          />
        </MFormField>

        <MSimpleGrid columns={4} gap={4} mb="4">
          <MFormField
            error={errors.linkedAccountId}
            label="Linked Account"
            tooltip="Optional account, usually a partner or reseller or customer. Used when quoting on behalf of another party."
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={linkedAccount?.accountName}
            >
              {isEditMode &&
              !linkedAccount &&
              isReadOnlyOrLockedProps.isReadOnly ? (
                <BlankText />
              ) : (
                <Controller
                  name="linkedAccountId"
                  control={control}
                  render={({ field }) => (
                    <AccountSelect
                      onClearAccountValue={() =>
                        handleChangeLinkedAccount(null)
                      }
                      value={linkedAccount}
                      onChange={(event) => {
                        handleChangeLinkedAccount(
                          event as unknown as IAccountRespSchema,
                        );
                      }}
                      disabledIds={[billGroup.accountId!]}
                      w="100%"
                      {...isReadOnlyOrLockedProps}
                      isDisabled={
                        isReadOnlyOrLockedProps.disabled ||
                        canOnlyUpdatePONumber
                      }
                      inputProps={{
                        variant: isReadOnlyOrLockedProps.variant,
                        readOnly: isReadOnlyOrLockedProps.isReadOnly,
                        disabled: isReadOnlyOrLockedProps.isDisabled,
                      }}
                      returnItem
                    />
                  )}
                />
              )}
            </MLockedTextOrContent>
          </MFormField>
        </MSimpleGrid>

        <MSimpleGrid columns={4} gap={4}>
          <MFormField
            error={errors.billingAccountId}
            label="Billing Account"
            tooltip="This is the account that will receive the invoice and handle payments. All billing contacts, addresses, and payment methods must come from this account."
            isRequired
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={billingAccount?.accountName}
            >
              <Controller
                name="billingAccountId"
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <MCustomSelect
                    itemTitle="accountName"
                    itemValue="id"
                    items={accountSelectItems}
                    useMainInputAsSearchInput
                    MainInputComponent={MainSearchInput}
                    renderItemContent={renderItemContentForAccount}
                    isSelectOpen={false}
                    value={value}
                    onChange={(val) => {
                      onChange(val);
                      if (val !== value) {
                        setValue('contactId', '', { shouldDirty: true });
                        setValue('addressData.billingAddressId', '', {
                          shouldDirty: true,
                        });
                      }
                    }}
                    {...isReadOnlyOrLockedProps}
                    isDisabled={
                      isReadOnlyOrLockedProps.disabled ||
                      canOnlyUpdatePONumber ||
                      !linkedAccountId
                    }
                    inputProps={{
                      variant: isReadOnlyOrLockedProps.variant,
                      readOnly: isReadOnlyOrLockedProps.isReadOnly,
                      disabled: isReadOnlyOrLockedProps.isDisabled,
                    }}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>

          <MFormField
            error={errors.contactId}
            label="Billing Contact"
            isRequired
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={contactIdCustomValue?.internalValue?.fullName}
            >
              <Controller
                name="contactId"
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <ContactSelect
                    showAddContacts={canDo([['account_contacts', 'create']])}
                    accountId={billingAccountId}
                    loading={contactIdCustomValue.isLoading}
                    value={contactIdCustomValue.internalValue}
                    returnItem
                    onChange={(val) => {
                      contactIdCustomValue.onInternalValueChange(val as any);
                    }}
                    clearable={false}
                    {...rest}
                    isReadOnly={isReadOnlyProps.isReadOnly}
                    isDisabled={
                      isReadOnlyProps.disabled || canOnlyUpdatePONumber
                    }
                    inputProps={{
                      variant: isReadOnlyProps.variant,
                      readOnly: isReadOnlyProps.isReadOnly,
                      disabled: isReadOnlyProps.isDisabled,
                    }}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>

          <MFormField
            error={errors.addressData?.billingAddressId}
            label="Billing Address"
          >
            <MLockedTextOrContent
              isLocked={!isEditMode || isAddressSourceIsContact}
              text={
                isAddressSourceIsContact
                  ? billingAddressTextForAddressSourceContact
                  : getAddress(billGroup.billingAddress).fullAddress
              }
            >
              <Controller
                name="addressData.billingAddressId"
                control={control}
                render={({ field }) => {
                  return (
                    <AccountAddressSelect
                      addressFormat={selectedLegalEntity?.addressFormat}
                      accountId={billingAccountId}
                      {...field}
                      {...isReadOnlyProps}
                      isDisabled={
                        isReadOnlyProps.disabled || canOnlyUpdatePONumber
                      }
                      inputProps={{
                        variant: isReadOnlyProps.variant,
                        readOnly: isReadOnlyProps.isReadOnly,
                        disabled: isReadOnlyProps.isDisabled,
                      }}
                    />
                  );
                }}
              />
            </MLockedTextOrContent>
          </MFormField>
          <MBox />
          <MFormField
            error={errors.shippingContactId}
            label="Shipping Account"
            tooltip="This is the account that is using the services and is used to determine taxes."
            isRequired
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={shippingAccount?.accountName}
            >
              <MCustomSelect
                itemTitle="accountName"
                itemValue="id"
                items={accountSelectItems}
                useMainInputAsSearchInput
                MainInputComponent={MainSearchInput}
                renderItemContent={renderItemContentForAccount}
                isSelectOpen={false}
                value={shippingAccountId}
                onChange={(val) => {
                  setShippingAccountId(val);
                  if (val !== shippingAccountId) {
                    setValue('shippingContactId', '', {
                      shouldDirty: true,
                    });
                    setValue('addressData.shippingAddressId', '', {
                      shouldDirty: true,
                    });
                  }
                }}
                {...isReadOnlyOrLockedProps}
                isDisabled={
                  isReadOnlyOrLockedProps.disabled ||
                  canOnlyUpdatePONumber ||
                  !linkedAccountId
                }
                inputProps={{
                  variant: isReadOnlyOrLockedProps.variant,
                  readOnly: isReadOnlyOrLockedProps.isReadOnly,
                  disabled: isReadOnlyOrLockedProps.isDisabled,
                }}
              />
            </MLockedTextOrContent>
          </MFormField>

          <MFormField
            error={errors.shippingContactId}
            label="Shipping Contact"
            isRequired
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={shippingContactIdCustomValue.internalValue?.fullName}
            >
              <Controller
                name="shippingContactId"
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <ContactSelect
                    showAddContacts={canDo([['account_contacts', 'create']])}
                    accountId={shippingAccountId}
                    loading={shippingContactIdCustomValue.isLoading}
                    value={shippingContactIdCustomValue.internalValue}
                    returnItem
                    onChange={(val) => {
                      shippingContactIdCustomValue.onInternalValueChange(
                        val as any,
                      );
                    }}
                    clearable={false}
                    {...rest}
                    {...isReadOnlyProps}
                    isDisabled={
                      isReadOnlyProps.disabled || canOnlyUpdatePONumber
                    }
                    inputProps={{
                      variant: isReadOnlyProps.variant,
                      readOnly: isReadOnlyProps.isReadOnly,
                      disabled: isReadOnlyProps.isDisabled,
                    }}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
          <MFormField
            error={errors.addressData?.shippingAddressId}
            label="Shipping Address"
          >
            <MLockedTextOrContent
              isLocked={!isEditMode || isAddressSourceIsContact}
              text={
                isAddressSourceIsContact
                  ? shippingAddressTextForAddressSourceContact
                  : getAddress(billGroup.shippingAddress).fullAddress
              }
            >
              <Controller
                name="addressData.shippingAddressId"
                control={control}
                render={({ field }) => {
                  return (
                    <AccountAddressSelect
                      addressFormat={selectedLegalEntity?.addressFormat}
                      accountId={shippingAccountId}
                      {...field}
                      {...isReadOnlyProps}
                      isDisabled={
                        isReadOnlyProps.disabled || canOnlyUpdatePONumber
                      }
                      inputProps={{
                        variant: isReadOnlyProps.variant,
                        readOnly: isReadOnlyProps.isReadOnly,
                        disabled: isReadOnlyProps.isDisabled,
                      }}
                    />
                  );
                }}
              />
            </MLockedTextOrContent>
          </MFormField>
          <MBox />
        </MSimpleGrid>

        <MFormField
          error={errors.strCcEmails}
          label="CC Emails"
          tooltip="Invoices will be sent to these emails. Use commas to add multiple emails."
          data-testid="email-text-area"
          mt={4}
        >
          <MLockedTextOrContent isLocked={!isEditMode} text={strCcEmails}>
            <Controller
              name="strCcEmails"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MTextarea
                  value={value || ''}
                  placeholder="Use commas to add multiple emails"
                  spellCheck={false}
                  rows={1}
                  {...rest}
                  {...isReadOnlyProps}
                  disabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>

        <MFormField
          error={errors.strBccEmails}
          label="BCC Emails"
          tooltip="Invoices will be sent to these emails. Use commas to add multiple emails."
          data-testid="bcc-email-text-area"
          mt={4}
        >
          <MLockedTextOrContent isLocked={!isEditMode} text={strBccEmails}>
            <Controller
              name="strBccEmails"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MTextarea
                  value={value || ''}
                  placeholder="Use commas to add multiple emails"
                  spellCheck={false}
                  rows={1}
                  {...rest}
                  {...isReadOnlyProps}
                  disabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MBox>
      <MDivider />

      <SectionHeader
        label="Invoice Settings"
        canEdit={canEditInvoiceTransformationConfig}
        additionalButtons={
          canEditInvoiceTransformationConfig &&
          !isEditMode && (
            <MButton
              size="sm"
              variant="secondary"
              onClick={onOpenInvoiceTransformationDrawer}
            >
              Output Configuration
            </MButton>
          )
        }
      />
      <MSimpleGrid columns={4} gap={4} mb={6}>
        <MFormField label="Next Invoice Date">
          <MLockedTextOrContent
            isLocked
            text={toDateShort(billGroup.nextInvoiceDate)}
          />
        </MFormField>
        <MFormField label="Invoice Template">
          <MLockedTextOrContent
            isLocked={!isEditMode}
            text={invoiceTemplateId || 'Default'}
          >
            <MCustomSelect
              placeholder="Select"
              items={['Default', 'Template 1', 'Template 2']}
              clearable
              value={'Default'}
              {...isReadOnlyOrLockedProps}
              isDisabled
              inputProps={{
                variant: isReadOnlyOrLockedProps.variant,
                readOnly: isReadOnlyOrLockedProps.isReadOnly,
                disabled: isReadOnlyOrLockedProps.isDisabled,
              }}
            />
          </MLockedTextOrContent>
        </MFormField>
        <MFormField label="Invoice Frequency">
          <MLockedTextOrContent
            isLocked
            text={
              invoicingFrequencyDisplay ? invoicingFrequencyDisplay.label : ''
            }
          />
        </MFormField>
        <MFormField error={errors.invoiceCreation} label="Create Invoices">
          <MLockedTextOrContent
            isLocked={!isEditMode}
            text={SETTING_INVOICE_CREATION_DISPLAY[invoiceCreation]}
          >
            <Controller
              name="invoiceCreation"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  items={SETTING_INVOICE_CREATION_OPTIONS}
                  {...field}
                  {...isReadOnlyProps}
                  isDisabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
                  inputProps={{
                    variant: isReadOnlyProps.variant,
                    readOnly: isReadOnlyProps.isReadOnly,
                    disabled: isReadOnlyProps.isDisabled,
                  }}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>

        <MBox>
          <MFormField
            error={errors?.firstInvoiceCreationStrategy}
            onClick={(ev) => ev.stopPropagation()}
            mt="2"
          >
            <Controller
              name="firstInvoiceCreationStrategy"
              control={control}
              render={({ field: { onChange, value, ...rest } }) => (
                <MCheckbox
                  isChecked={
                    value ===
                    FirstInvoiceCreationStrategyEnum.GENERATE_ON_QUOTE_PROCESSED
                  }
                  isReadOnly={isReadOnlyProps.isReadOnly}
                  {...rest}
                  isDisabled={
                    !isEditMode ||
                    isReadOnlyProps.disabled ||
                    canOnlyUpdatePONumber
                  }
                  onChange={(ev) => {
                    if (ev.target.checked) {
                      onChange(
                        FirstInvoiceCreationStrategyEnum.GENERATE_ON_QUOTE_PROCESSED,
                      );
                    } else {
                      onChange(
                        FirstInvoiceCreationStrategyEnum.FOLLOW_EXISTING_SETTINGS,
                      );
                    }
                  }}
                >
                  Automatically create the first invoice of Contracts upon quote
                  processing.
                </MCheckbox>
              )}
            />
          </MFormField>
        </MBox>
        {invoiceCreation !== InvoiceCreationEnum.MANUAL && (
          <MFormField
            error={errors.invoiceDelay}
            label="Offset from Billing Schedule"
            tooltip={INVOICE_DELAY_TOOLTIP}
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={formatInvoiceDelay(invoiceDelay)}
            >
              <Controller
                name="invoiceDelay"
                control={control}
                render={({
                  field: { value, onChange, onBlur, name: fieldName },
                }) => (
                  <InvoiceDelayInput
                    name={fieldName}
                    value={value}
                    onChange={(newInvoiceDelay) => {
                      onChange(newInvoiceDelay);
                      setInvoiceDelay(newInvoiceDelay);
                    }}
                    onBlur={onBlur}
                    isDisabled={
                      isReadOnlyProps.disabled || canOnlyUpdatePONumber
                    }
                    isReadOnly={isReadOnlyProps.isReadOnly}
                    variant={isReadOnlyProps.variant}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
        )}

        {showInvoiceAccountingDate && (
          <MFormField
            error={errors.accountingDateCalculationStrategy}
            label="Bill Date Calculation Strategy"
            tooltip={
              <MTooltipContentList
                title={invoiceBillDateSettingsTooltipTitle}
                listItems={invoiceBillDateSettingsTooltipItems}
              />
            }
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={
                SETTING_INVOICE_ACCOUNTING_DATE_OPTIONS.find(
                  ({ value }) => value === accountingDateCalculationStrategy,
                )?.title
              }
            >
              <Controller
                name="accountingDateCalculationStrategy"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    items={SETTING_INVOICE_ACCOUNTING_DATE_OPTIONS}
                    {...field}
                    {...isReadOnlyProps}
                    isDisabled={
                      isReadOnlyProps.disabled || canOnlyUpdatePONumber
                    }
                    inputProps={{
                      variant: isReadOnlyProps.variant,
                      readOnly: isReadOnlyProps.isReadOnly,
                      disabled: isReadOnlyProps.isDisabled,
                    }}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
        )}

        <MFormField
          error={errors.autoEmailInvoice}
          label="Email Customer Invoices"
        >
          <Controller
            name="autoEmailInvoice"
            control={control}
            render={({ field: { value, onChange, ...rest } }) => {
              return (
                <MRadioGroup
                  value={typeof value === 'undefined' ? 'true' : String(value)}
                  onChange={(val) => {
                    onChange(val === 'true');
                  }}
                  minH={8}
                  display="flex"
                  alignItems="center"
                  {...rest}
                  isDisabled={
                    !isEditMode ||
                    isReadOnlyProps.disabled ||
                    canOnlyUpdatePONumber
                  }
                >
                  <MStack direction="row" columnGap={5}>
                    <MRadio value="true">
                      Automatically
                      {!!value && (
                        <MTooltip
                          shouldWrapChildren
                          label={
                            'MonetizeNow will email your customers automatically when invoices are created.'
                          }
                          placement="bottom-end"
                        >
                          <MIcon
                            as={MdOutlineError}
                            w={3}
                            h={3}
                            ml={1}
                            color={'tOrange.tangerine'}
                          />
                        </MTooltip>
                      )}
                    </MRadio>
                    <MRadio value="false">Manually</MRadio>
                  </MStack>
                </MRadioGroup>
              );
            }}
          />
        </MFormField>
        {/* <MFormField label="Invoice Memo">
        <MLockedTextOrContent isLocked={!isEditMode} text={name}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <MInput
                placeholder=""
                maxLength={60}
                isDisabled={canOnlyUpdatePONumber}
                {...field}
              />
            )}
          />
        </MLockedTextOrContent>
      </MFormField> */}
      </MSimpleGrid>

      <MDivider />

      <SectionHeader label="Payment Settings" />
      <MBox mb={6}>
        <MFormField error={errors.collectInvoiceBalanceAutomatically} mb={4}>
          <Controller
            name="collectInvoiceBalanceAutomatically"
            defaultValue={false}
            control={control}
            render={({ field: { value, ...rest } }) => (
              <MCheckbox
                pt={2}
                pb={1}
                isChecked={value}
                isReadOnly={isReadOnlyProps.isReadOnly}
                {...rest}
                isDisabled={
                  !isEditMode ||
                  isReadOnlyProps.disabled ||
                  canOnlyUpdatePONumber
                }
              >
                Collect Invoice Automatically
              </MCheckbox>
            )}
          />
        </MFormField>
        <MSimpleGrid columns={4} gap={4}>
          {enableAutoPaymentTiming && (
            <MFormField
              error={errors.autoPaymentCollectionTiming}
              label="Auto-Payment Timing"
            >
              <MLockedTextOrContent
                isLocked={!isEditMode}
                text={
                  AUTO_PAYMENT_COLLECTION_TIMING_DISPLAY[
                    autoPaymentCollectionTiming
                  ] ?? ''
                }
              >
                <Controller
                  name="autoPaymentCollectionTiming"
                  control={control}
                  render={({ field }) => (
                    <MCustomSelect
                      placeholder="Select"
                      items={AUTO_PAYMENT_COLLECTION_TIMING_OPTIONS}
                      {...field}
                      isDisabled={
                        !collectInvoiceBalanceAutomatically ||
                        !isEditMode ||
                        isReadOnlyProps.disabled ||
                        canOnlyUpdatePONumber
                      }
                    />
                  )}
                />
              </MLockedTextOrContent>
            </MFormField>
          )}
          <MFormField error={errors.billDay} label="Bill Cycle Day" isRequired>
            <MLockedTextOrContent
              isLocked={!isEditMode || billGroup.locked}
              text={billDay}
            >
              <Controller
                name="billDay"
                defaultValue={Number(formatDate(new Date(), 'd'))}
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    placeholder="Select"
                    items={[...Array(31).keys()].map((k) => ({
                      title: (k + 1).toString(),
                      value: k + 1,
                    }))}
                    {...field}
                    {...isReadOnlyProps}
                    isDisabled={
                      isReadOnlyProps.disabled || canOnlyUpdatePONumber
                    }
                    inputProps={{
                      variant: isReadOnlyProps.variant,
                      readOnly: isReadOnlyProps.isReadOnly,
                      disabled: isReadOnlyProps.isDisabled,
                    }}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
          <MFormField
            error={errors.defaultPaymentGatewayId}
            label="Payment Gateway"
          >
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={
                (defaultPaymentGatewayId &&
                  gatewaysById[defaultPaymentGatewayId]?.description) ||
                ''
              }
            >
              <Controller
                name="defaultPaymentGatewayId"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    placeholder="Select"
                    items={activeGateways.map(({ id, description }: any) => ({
                      title: description,
                      value: id,
                    }))}
                    {...field}
                    {...isReadOnlyOrLockedProps}
                    isDisabled={
                      isReadOnlyOrLockedProps.disabled || canOnlyUpdatePONumber
                    }
                    inputProps={{
                      variant: isReadOnlyOrLockedProps.variant,
                      readOnly: isReadOnlyOrLockedProps.isReadOnly,
                      disabled: isReadOnlyOrLockedProps.isDisabled,
                    }}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>

          <MFormField error={errors.paymentMethodId} label="Payment Method">
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={
                (paymentMethodId &&
                  paymentMethods?.paymentMethodsById[paymentMethodId]
                    ?.paymentMethodName) ||
                ''
              }
            >
              <Controller
                name="paymentMethodId"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    placeholder="Select"
                    itemValue="id"
                    itemTitle="paymentMethodName"
                    items={
                      defaultPaymentGatewayId
                        ? paymentMethods?.paymentMethodsByGatewayId?.[
                            defaultPaymentGatewayId
                          ] || []
                        : []
                    }
                    prependItems={
                      (defaultPaymentGatewayId &&
                        currentPaymentGateway && [
                          {
                            item: PAYMENT_METHODS.NEW_PAYMENT_METHOD,
                            isAction: true,
                            hasDivider: true,
                            onClick: ({ onClose }) => {
                              onClose && onClose();
                              onOpenPaymentMethodModal();
                            },
                          },
                        ]) ||
                      []
                    }
                    renderItemContent={PaymentMethodItemRenderer}
                    clearable
                    {...field}
                    {...isReadOnlyProps}
                    isDisabled={
                      isReadOnlyProps.disabled || canOnlyUpdatePONumber
                    }
                    inputProps={{
                      variant: isReadOnlyProps.variant,
                      readOnly: isReadOnlyProps.isReadOnly,
                      disabled: isReadOnlyProps.isDisabled,
                    }}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
          <MFormField error={errors.netTerms} label="Net Terms" isRequired>
            <MLockedTextOrContent
              isLocked={!isEditMode}
              text={NET_TERMS_DISPLAY[netTerms]}
            >
              <Controller
                name="netTerms"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    placeholder="Select Net Terms"
                    items={activeNetTerms}
                    itemTitle="name"
                    itemValue="value"
                    {...field}
                    {...isReadOnlyOrLockedProps}
                    isDisabled={
                      isReadOnlyOrLockedProps.disabled || canOnlyUpdatePONumber
                    }
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
        </MSimpleGrid>
      </MBox>

      <MDivider />
      <SectionHeader label="Dunning" />
      <MSimpleGrid columns={4} gap={4} mb={6}>
        <MFormField error={errors.dunningProcessId} label="Dunning Process">
          <MLockedTextOrContent
            isLocked={!isEditMode}
            text={
              dunningProcessId &&
              dunningData?.contentById[dunningProcessId]?.name
            }
          >
            <Controller
              name="dunningProcessId"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  placeholder="Select"
                  items={(dunningData?.content || []).map((x: IDunnings) => ({
                    title: x.name,
                    value: x.id,
                  }))}
                  clearable
                  onClear={() => {
                    setValue('dunningProcessId', null, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  {...field}
                  {...isReadOnlyProps}
                  isDisabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
                  inputProps={{
                    variant: isReadOnlyProps.variant,
                    readOnly: isReadOnlyProps.isReadOnly,
                    disabled: isReadOnlyProps.isDisabled,
                  }}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
        <MFormField label="Status">
          <MLockedTextOrContent
            isLocked
            text={billGroup.dunningStepId ? 'In Dunning' : 'Not In Dunning'}
          />
        </MFormField>
        <MFormField label="Current Dunning Step">
          <MLockedTextOrContent isLocked text={dunningStep?.stepName || ''} />
        </MFormField>
      </MSimpleGrid>

      {isAnyCustomFieldsExists && (
        <>
          <MDivider />
          <SectionHeader label="Custom Fields" />

          <Controller
            name="customFields"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CustomFieldDataForm
                entity={CustomFieldEntityEnum.BILL_GROUP}
                value={value}
                setValue={(val) => {
                  onChange(val);
                }}
                fieldColSpan={6}
                mode="page"
                {...isReadOnlyProps}
                isReadOnly={!isEditMode || isReadOnlyProps.isReadOnly}
                variant={
                  !isEditMode || isReadOnlyProps.isReadOnly
                    ? 'readonly'
                    : 'outline'
                }
                isDisabled={isReadOnlyProps.disabled || canOnlyUpdatePONumber}
              />
            )}
          />
        </>
      )}

      <AddPaymentMethodForm
        isOpen={isPaymentMethodModalOpen}
        accountId={billGroup.accountId}
        onClose={(newPaymentMethod) =>
          onClosePaymentMethodModal(newPaymentMethod)
        }
      />
    </MBox>
  );
};
